// Deleting a secret cannot be undone, so it asks first.
import { Trash } from "@phosphor-icons/react";
import { useState } from "react";
import { Button, Modal } from "rizzui";

import { useT } from "../i18n";
import type { Credential } from "./templates";

interface Props {
  credential: Credential | undefined;
  onClose: () => void;
  onConfirm: (credential: Credential) => Promise<void>;
}

export function DeleteSecretDialog({ credential, onClose, onConfirm }: Props) {
  const { common, secrets } = useT();
  const copy = secrets.forms.remove;
  const [busy, setBusy] = useState(false);
  const name = credential?.name ?? "";
  async function confirm() {
    if (!credential) return;
    setBusy(true);
    try {
      await onConfirm(credential);
    } finally {
      setBusy(false);
      onClose();
    }
  }
  return (
    <Modal isOpen={!!credential} onClose={onClose} containerClassName="dark:bg-gray-100">
      <div className="p-6" role="group" aria-label={copy.label(name)}>
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-lighter text-red-dark">
            <Trash weight="duotone" className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 className="text-lg">{copy.question(<span className="font-mono">{name}</span>)}</h2>
            <p className="mt-1 text-[13px] text-gray-500">{copy.body}</p>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {common.actions.cancel}
          </Button>
          <Button color="danger" isLoading={busy} aria-label={copy.confirm(name)} onClick={() => void confirm()}>
            {common.actions.delete}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
